import PropTypes from "prop-types"
import { Row, Col } from "antd"
import { MediaPreview } from "ui/MediaPreview"
import { PreviewGallery } from "ui/PersonalInfo/ProfilePreview/PreviewGallery"
import styles from "./MediaGrid.module.css"

const getImageSrc = (media) =>
  media.thumbnail_url || media.preview_url || media.placeholder_url || media.url

export default function MediaGrid({ photos = [], handleMediaClick, guideName = "", embedded = false }) {
  if (!photos.length) return null

  const renderMedia = (media, idx) => (
    <div key={media?.id || idx} className={styles.mediaWrapper}>
      <img src={getImageSrc(media)} alt="" className={styles.blurredBackground} />
      <div className={styles.mediaContent}>
        <MediaPreview
          media={media}
          src={media.url}
          alt=""
          className="w-100 h-100"
          effect="blur"
          placeholderSrc={media.placeholder_url || null}
          onClick={() => handleMediaClick?.(media)}
        />
      </div>
    </div>
  )

  if (photos.length === 1) {
    return (
      <Row className={styles.gridRow}>
        <Col span={24} style={{ height: embedded ? 400 : 460 }}>
          {renderMedia(photos[0], 0)}
        </Col>
      </Row>
    )
  }

  if (photos.length === 2) {
    return (
      <Row gutter={8} className={styles.gridRow}>
        <Col span={12} className={styles.fullHeightCol}>{renderMedia(photos[0], 0)}</Col>
        <Col span={12} className={styles.fullHeightCol}>{renderMedia(photos[1], 1)}</Col>
      </Row>
    )
  }

  // 3+ photos: 1 large left, up to 4 in 2×2 right
  const rightPhotos = photos.slice(1, 5)

  return (
    <Row gutter={8} className={styles.gridRow}>
      <Col span={12} className={styles.fullHeightCol}>
        {renderMedia(photos[0], 0)}
      </Col>
      <Col span={12} className={styles.fullHeightCol}>
        <Row gutter={[8, 8]} className={styles.fullHeightRow}>
          {rightPhotos.map((photo, i) => {
            const isLastCell = i === rightPhotos.length - 1 && photos.length > 5
            return (
              <Col key={photo?.id || `grid-${i + 1}`} span={12} className={styles.halfHeightCol}>
                <div className={styles.cellInner} style={{ position: isLastCell ? "relative" : undefined }}>
                  {isLastCell && (
                    <div className="position-absolute bottom-0 end-0 p-3 z-2">
                      <PreviewGallery name={guideName} mediaItems={photos} hideGrid />
                    </div>
                  )}
                  {renderMedia(photo, i + 1)}
                </div>
              </Col>
            )
          })}
          {Array.from({ length: 4 - rightPhotos.length }, (_, i) => `empty-${rightPhotos.length + i}`).map((key) => (
            <Col key={key} span={12} className={styles.halfHeightCol} />
          ))}
        </Row>
      </Col>
    </Row>
  )
}

MediaGrid.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      url: PropTypes.string.isRequired,
      placeholder_url: PropTypes.string,
      thumbnail_url: PropTypes.string,
      preview_url: PropTypes.string
    })
  ),
  handleMediaClick: PropTypes.func,
  guideName: PropTypes.string,
  embedded: PropTypes.bool
}
